import { useCallback, useState } from "react";
import usePaginatedData from "~/hooks/usePaginatedData";
import APIRequest from "~/lib/api_request";

// Raised with the react-virtuoso InfiniteScroll migration — virtuoso
// virtualises DOM nodes not data, so large in-memory activity arrays
// are cheap. Cap is kept as a sanity ceiling, not a performance limit.
const MAX_ACTIVITIES = 100_000;

const useActivities = (leadId, activeTab, anchorActivityId = null) => {
  const [pinnedActivities, setPinnedActivities] = useState([]);
  const [pinnedCount, setPinnedCount] = useState(0);
  const buildRequestData = useCallback(
    (pageToFetch, useAnchor, anchorId) => {
      const requestData = {
        type: activeTab,
        limit: 10,
      };

      if (useAnchor && anchorId) {
        requestData.anchor_activity_id = anchorId;
      } else {
        requestData.page = pageToFetch;
      }

      return requestData;
    },
    [activeTab],
  );

  const fetchFn = useCallback(
    (requestData) =>
      APIRequest.get({
        resource: `/v1/leads/${leadId}/activities`,
        data: requestData,
      }),
    [leadId],
  );

  const parseResponse = useCallback((response) => {
    const {
      activities: leadActivities,
      pinned_activities: pinnedData,
      pinned_count: serverPinnedCount,
      pagy,
    } = response.body;

    // Defensive check: handle null/undefined activities gracefully
    const fetchedActivities = leadActivities ? JSON.parse(leadActivities) : [];

    // Parse pinned activities sidecar (full replacement, not append)
    const fetchedPinned = pinnedData ? JSON.parse(pinnedData) : [];
    setPinnedActivities(fetchedPinned);
    setPinnedCount(serverPinnedCount || 0);

    // Convert pagy format to standard pagination format
    // Supports both built-in Pagy (page, last) and legacy format (current_page, last_page)
    const currentPage = pagy?.page || pagy?.current_page;
    const lastPage = pagy?.last || pagy?.last_page;

    return {
      items: fetchedActivities,
      pagination: {
        page: currentPage,
        nextPage: currentPage < lastPage ? currentPage + 1 : null,
        prevPage: currentPage > 1 ? currentPage - 1 : null,
      },
    };
  }, []);

  const {
    items: activities,
    loading: loadingActivities,
    loadingPrev: loadingPrevActivities,
    error,
    nextPage,
    prevPage,
    prevItemsLength: prevActivitiesLength,
    setItems: setActivities,
    setLoading: setLoadingActivities,
    loadMore: loadMoreActivities,
    fetch: fetchActivities,
  } = usePaginatedData({
    fetchFn,
    resourceId: leadId,
    dependencies: [activeTab, anchorActivityId],
    anchorId: anchorActivityId,
    parseResponse,
    buildRequestData,
    maxItems: MAX_ACTIVITIES,
    shouldFetch: !!leadId,
  });

  return {
    activities,
    setActivities,
    loadingActivities,
    loadingPrevActivities,
    setLoadingActivities,
    error,
    nextPage,
    prevPage,
    loadMoreActivities,
    fetchActivities,
    prevActivitiesLength,
    pinnedActivities,
    setPinnedActivities,
    pinnedCount,
    setPinnedCount,
  };
};

export default useActivities;
